import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { CommandItem } from '../commands/types'
import { useFullPartsBOM } from '@/pages/DocumentPage/components/OperationDocument/hooks/useBOM'
import { flattenToolPages, useListToolsQuery } from '@/services/queries/tools'
import commands from '../commands'

export const useCommandItems = ({
  commandName,
  documentPageId,
  query,
}: {
  commandName: string
  documentPageId: string
  query: string
}) => {
  const bom = useFullPartsBOM()
  const toolsSearchQuery = useMemo(() => {
    /**
     * Slash command queries arrive as `tools:<typed text>`.
     * We strip only the command prefix so backend search follows live typing.
     */
    if (commandName !== 'tools') return ''
    return query.replace(/^\/?tools:/, '').trimStart()
  }, [commandName, query])
  const { data: toolsData, dataUpdatedAt: toolsDataUpdatedAt } =
    useListToolsQuery({
      search: toolsSearchQuery,
      limit: 100,
      offset: 0,
      // Slash search must stay org-wide; do not scope by document version here.
      enabled: commandName === 'tools',
    })
  const tools = useMemo(() => flattenToolPages(toolsData), [toolsData])

  return useQuery({
    staleTime: 0,
    // Include tools query freshness so tools slash results recompute whenever
    // the backing tools list changes (rename/create/delete).
    queryKey: [
      'editorCommands',
      commandName,
      documentPageId,
      toolsSearchQuery,
      toolsDataUpdatedAt,
    ],
    queryFn: async (): Promise<CommandItem[]> => {
      const command = commands.find((command) => command.name === commandName)
      if (!command) {
        return []
      }
      return command.getItems({
        documentPageId,
        bom,
        tools,
      })
    },
  })
}
